import { useEffect, useMemo, useState } from "react";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { collection, onSnapshot, orderBy, query, where } from "firebase/firestore";
import {
  Alert,
  Button,
  Calendar,
  Card,
  Col,
  Empty,
  Flex,
  Form,
  Input,
  Layout,
  Row,
  Space,
  Spin,
  Steps,
  Tag,
  Typography,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import {
  COLLECTIONS,
  DEFAULT_BOOKING_HORIZON_DAYS,
  formatNok,
  type BookingManifest,
  type BookingType,
  type ManifestDateOverride,
  type BookingAvailabilityResponse,
  type CreateBookingResponse,
  type StartBookingPaymentResponse,
} from "@gull/shared";
import { useAuth } from "../lib/auth";
import { enduserApi, ApiError } from "../lib/api";
import { db } from "../lib/firebase";
import { ErrorAlert } from "@gull/ui";

const { Content } = Layout;
const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

const TIME_FMT = new Intl.DateTimeFormat("nb-NO", {
  timeZone: "Europe/Oslo",
  hour: "2-digit",
  minute: "2-digit",
});

const DATE_LONG_FMT = new Intl.DateTimeFormat("nb-NO", {
  timeZone: "Europe/Oslo",
  weekday: "long",
  day: "numeric",
  month: "long",
});

const PHONE_RE = /^\+?\d{8,15}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface PreRegistrationDraft {
  id: string;
  description: string;
}

function makePreRegistrationDraft(): PreRegistrationDraft {
  return {
    id: globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`,
    description: "",
  };
}

function hasPreRegistrationDetails(item: PreRegistrationDraft): boolean {
  return item.description.trim().length > 0;
}

function isDayOpen(
  date: Dayjs,
  manifest: Pick<BookingManifest, "businessHours" | "dateOverrides">,
): boolean {
  const dateStr = date.format("YYYY-MM-DD");
  const override = manifest.dateOverrides?.find((o) => o.date === dateStr);
  if (override) return !override.closed;
  const weekday = date.day();
  const weekly = manifest.businessHours.find((h) => h.dayOfWeek === weekday);
  return !!weekly && !weekly.closed;
}

function overrideReason(date: Dayjs, overrides: ManifestDateOverride[] | undefined): string | null {
  const dateStr = date.format("YYYY-MM-DD");
  const o = overrides?.find((x) => x.date === dateStr);
  if (!o?.closed) return null;
  return o.reason ?? "Stengt";
}

function capFirst(input: string) {
  return input.length > 0 ? input[0].toUpperCase() + input.slice(1) : input;
}

function formatCalendarMonth(value: Dayjs) {
  return capFirst(value.locale("nb").format("MMMM YYYY"));
}

function findFirstEnabledDateInMonth(
  month: Dayjs,
  manifest: BookingManifest | null,
  maxBookingDate: Dayjs,
) {
  const today = dayjs().startOf("day");
  const start = month.startOf("month").isBefore(today, "day") ? today : month.startOf("month");
  const end = month.endOf("month").isAfter(maxBookingDate, "day")
    ? maxBookingDate
    : month.endOf("month");
  for (let cursor = start; !cursor.isAfter(end, "day"); cursor = cursor.add(1, "day")) {
    if (!manifest || isDayOpen(cursor, manifest)) return cursor;
  }
  return start;
}

export default function Book() {
  const { loading } = useAuth();

  if (loading) {
    return (
      <Content style={{ padding: 48 }}>
        <Spin />
      </Content>
    );
  }
  return <BookingForm />;
}

function BookingForm() {
  const { user, signInWithVipps } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [manifests, setManifests] = useState<BookingManifest[] | null>(null);
  const [manifestsError, setManifestsError] = useState<string | null>(null);

  useEffect(() => {
    const q = query(
      collection(db, COLLECTIONS.bookingManifests),
      where("active", "==", true),
      orderBy("name"),
    );
    return onSnapshot(
      q,
      (snap) => setManifests(snap.docs.map((d) => d.data() as BookingManifest)),
      (err) => setManifestsError(err.message),
    );
  }, []);

  const [manifestId, setManifestId] = useState<string | null>(null);
  const [type, setType] = useState<BookingType | null>(null);
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [selectedSlot, setSelectedSlot] = useState<number | null>(null);
  const [notes, setNotes] = useState("");
  const [preRegistrationItems, setPreRegistrationItems] = useState<PreRegistrationDraft[]>([
    makePreRegistrationDraft(),
  ]);
  const [contact, setContact] = useState({
    name: user?.displayName ?? "",
    email: user?.email ?? "",
    phone: user?.phoneNumber ?? "",
  });

  useEffect(() => {
    if (!manifestId && manifests?.length) setManifestId(manifests[0].id);
  }, [manifestId, manifests]);

  const manifest = useMemo(
    () => manifests?.find((m) => m.id === manifestId) ?? null,
    [manifests, manifestId],
  );

  useEffect(() => {
    if (!manifest) return;
    const enabled = (
      Object.entries(manifest.types) as Array<[BookingType, { enabled: boolean }]>
    ).filter(([, t]) => t.enabled);
    if (!type || !enabled.find(([k]) => k === type)) {
      setType(enabled[0]?.[0] ?? null);
    }
  }, [manifest, type]);

  useEffect(() => {
    if (!manifest) return;
    if (!isDayOpen(date, manifest)) {
      let cursor = date;
      for (let i = 0; i < 30; i += 1) {
        cursor = cursor.add(1, "day");
        if (isDayOpen(cursor, manifest)) {
          setDate(cursor);
          break;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifestId]);

  useEffect(() => setSelectedSlot(null), [manifestId, type, date]);

  const dateStr = date.format("YYYY-MM-DD");
  const slotsQ = useQuery<BookingAvailabilityResponse>({
    queryKey: ["availability", manifestId, type, dateStr],
    queryFn: () =>
      enduserApi.publicGet<BookingAvailabilityResponse>(
        `/bookings/availability?manifestId=${manifestId!}&date=${dateStr}&type=${type!}`,
      ),
    enabled: !!manifestId && !!type,
  });

  const enabledTypeEntries = manifest
    ? (
        Object.entries(manifest.types) as Array<
          [
            BookingType,
            {
              label: string;
              description: string;
              durationMinutes: number;
              enabled: boolean;
              priceNok?: number;
            },
          ]
        >
      ).filter(([, t]) => t.enabled)
    : [];

  const currentType = type && manifest ? manifest.types[type] : null;
  const reasonClosed = manifest ? overrideReason(date, manifest.dateOverrides) : null;
  const dateIsClosed = manifest ? !isDayOpen(date, manifest) : false;
  const horizonDays = manifest?.bookingHorizonDays ?? DEFAULT_BOOKING_HORIZON_DAYS;
  const maxBookingDate = dayjs().add(horizonDays, "day");
  const dateBeyondHorizon = date.isAfter(maxBookingDate, "day");

  const nameError = contact.name.trim().length === 0 ? "Navn er påkrevd" : null;
  const emailError = !contact.email
    ? "E-post er påkrevd"
    : EMAIL_RE.test(contact.email)
      ? null
      : "Ugyldig e-postadresse";
  const phoneError = !contact.phone
    ? "Telefon er påkrevd"
    : PHONE_RE.test(contact.phone.replace(/\s/g, ""))
      ? null
      : "Norsk eller internasjonalt nummer, +47…";
  const normalizedPreRegistrationItems = useMemo(
    () =>
      preRegistrationItems.filter(hasPreRegistrationDetails).map((item) => ({
        id: item.id,
        description: item.description.trim() || undefined,
        estimatedKarat: null,
        estimatedWeightGrams: null,
        notes: undefined,
      })),
    [preRegistrationItems],
  );

  const loginRequired = !!manifest && manifest.requireLogin !== false && !user;
  const contactValid =
    contact.name.trim().length > 0 && !emailError && !phoneError && !loginRequired;

  const steps = useMemo(() => {
    const list: Array<{
      key: "sted" | "tjeneste" | "tid" | "detaljer" | "kontakt" | "bekreft";
      label: string;
    }> = [];
    if ((manifests?.length ?? 0) > 1) list.push({ key: "sted", label: "Sted" });
    if (enabledTypeEntries.length > 1) list.push({ key: "tjeneste", label: "Tjeneste" });
    list.push({ key: "tid", label: "Tid" });
    list.push({ key: "detaljer", label: "Gjenstander" });
    list.push({ key: "kontakt", label: "Kontakt" });
    list.push({ key: "bekreft", label: "Bekreft" });
    return list;
  }, [manifests, enabledTypeEntries.length]);

  const [stepIndex, setStepIndex] = useState(0);
  useEffect(() => {
    setStepIndex((i) => Math.min(i, Math.max(0, steps.length - 1)));
  }, [steps.length]);
  const activeStep = steps[stepIndex]?.key ?? "bekreft";

  const createMutation = useMutation({
    mutationFn: async (input: {
      manifestId: string;
      type: BookingType;
      scheduledAt: number;
      durationMinutes: number;
    }) => {
      const body = {
        manifestId: input.manifestId,
        type: input.type,
        scheduledAt: input.scheduledAt,
        durationMinutes: input.durationMinutes,
        customer: contact,
        notes: notes || undefined,
        preRegistrationItems:
          normalizedPreRegistrationItems.length > 0 ? normalizedPreRegistrationItems : undefined,
      };
      return user
        ? enduserApi.authedPost<CreateBookingResponse>("/bookings", body)
        : enduserApi.publicPost<CreateBookingResponse>("/bookings", body);
    },
    onSuccess: async (data) => {
      void queryClient.invalidateQueries({ queryKey: ["availability"] });
      if (data.paymentRequired) {
        try {
          const session = await enduserApi.authedPost<StartBookingPaymentResponse>(
            `/bookings/${data.id}/payment/start`,
            {},
          );
          window.location.assign(session.checkoutUrl);
          return;
        } catch (err) {
          if (err instanceof ApiError && err.code === "DINTERO_NOT_CONFIGURED") {
            navigate(`/min-side?bookingId=${data.id}`, { replace: true });
            return;
          }
          throw err;
        }
      }
      navigate("/min-side", { replace: true });
    },
  });

  const error = createMutation.error instanceof ApiError ? createMutation.error.message : null;
  const canSubmit =
    !!manifestId && !!type && !!selectedSlot && contactValid && !createMutation.isPending;

  if (manifestsError) {
    return (
      <Content style={{ maxWidth: 480, margin: "48px auto", padding: 24 }}>
        <Alert
          type="error"
          message="Kunne ikke laste bestillingssteder"
          description={manifestsError}
        />
      </Content>
    );
  }
  if (manifests === null) {
    return (
      <Content style={{ padding: 48 }}>
        <Spin />
      </Content>
    );
  }
  if (manifests.length === 0) {
    return (
      <Content style={{ maxWidth: 480, margin: "48px auto", padding: 24 }}>
        <Empty description="Ingen lokasjoner er tilgjengelige akkurat nå." />
      </Content>
    );
  }

  const stepValid = (key: typeof activeStep): boolean => {
    switch (key) {
      case "sted":
        return !!manifestId;
      case "tjeneste":
        return !!type;
      case "tid":
        return !!selectedSlot && !dateBeyondHorizon;
      case "detaljer":
        return true;
      case "kontakt":
        return contactValid;
      case "bekreft":
        return canSubmit;
    }
  };

  const jumpToNextOpenDay = () => {
    if (!manifest) return;
    let cursor = date;
    for (let i = 0; i < horizonDays; i += 1) {
      cursor = cursor.add(1, "day");
      if (cursor.isAfter(maxBookingDate, "day")) return;
      if (isDayOpen(cursor, manifest)) {
        setDate(cursor);
        return;
      }
    }
  };

  const disabledCalendarDate = (value: Dayjs) =>
    value.isBefore(dayjs(), "day") ||
    value.isAfter(maxBookingDate, "day") ||
    (manifest ? !isDayOpen(value, manifest) : false);

  const moveCalendarMonth = (value: Dayjs, onChange: (date: Dayjs) => void, direction: -1 | 1) => {
    const next = findFirstEnabledDateInMonth(
      value.startOf("month").add(direction, "month"),
      manifest,
      maxBookingDate,
    );
    onChange(next);
    setDate(next);
  };

  const submit = () => {
    if (!manifestId || !type || !selectedSlot || !currentType) return;
    createMutation.mutate({
      manifestId,
      type,
      scheduledAt: selectedSlot,
      durationMinutes: currentType.durationMinutes,
    });
  };

  const advance = () => {
    if (activeStep === "bekreft") {
      submit();
      return;
    }
    if (!stepValid(activeStep)) return;
    setStepIndex(Math.min(stepIndex + 1, steps.length - 1));
  };

  return (
    <Content
      style={{
        maxWidth: 720,
        margin: "0 auto",
        padding: "24px 16px calc(132px + env(safe-area-inset-bottom)) 16px",
      }}
    >
      <Title level={3} style={{ marginBottom: 4 }}>
        Bestill time
      </Title>
      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Du mottar bekreftelse på e-post.
      </Paragraph>

      <Steps
        size="small"
        responsive
        current={stepIndex}
        onChange={(i) => {
          if (i <= stepIndex) {
            setStepIndex(i);
            return;
          }
          for (let s = stepIndex; s < i; s += 1) {
            if (!stepValid(steps[s].key)) {
              setStepIndex(s);
              return;
            }
          }
          setStepIndex(i);
        }}
        items={steps.map((s) => ({ title: s.label }))}
        style={{ marginBottom: 20 }}
      />

      {activeStep === "sted" ? (
        <Card size="small" title="Velg sted">
          <Row gutter={[12, 12]}>
            {manifests.map((m) => {
              const active = manifestId === m.id;
              return (
                <Col key={m.id} xs={24} sm={12}>
                  <Card
                    hoverable
                    onClick={() => setManifestId(m.id)}
                    style={{
                      borderColor: active ? "#c79d3a" : undefined,
                      background: active ? "#fdf9ef" : undefined,
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{m.name}</div>
                    {m.description ? (
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        {m.description}
                      </Text>
                    ) : null}
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card>
      ) : null}

      {activeStep === "tjeneste" ? (
        <Card size="small" title="Velg tjeneste">
          {enabledTypeEntries.length === 0 ? (
            <Text type="secondary">Ingen tjenester tilgjengelig.</Text>
          ) : (
            <Row gutter={[12, 12]}>
              {enabledTypeEntries.map(([key, t]) => {
                const active = type === key;
                return (
                  <Col key={key} xs={24} sm={12}>
                    <Card
                      hoverable
                      onClick={() => setType(key)}
                      style={{
                        borderColor: active ? "#c79d3a" : undefined,
                        background: active ? "#fdf9ef" : undefined,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "baseline",
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>{t.label}</div>
                        {t.priceNok && t.priceNok > 0 ? (
                          <Text strong>{formatNok(t.priceNok)}</Text>
                        ) : null}
                      </div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t.durationMinutes} min
                        {t.priceNok && t.priceNok > 0 ? " · betales ved booking" : null}
                      </Text>
                      <Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
                        {t.description}
                      </Paragraph>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Card>
      ) : null}

      {activeStep === "tid" ? (
        <Card size="small" title="Velg dato og tidspunkt">
          <Space direction="vertical" size={18} style={{ display: "flex" }}>
            <Calendar
              value={date}
              fullscreen={false}
              validRange={[dayjs(), maxBookingDate]}
              disabledDate={disabledCalendarDate}
              onSelect={(next) => setDate(next)}
              headerRender={({ value, onChange }) => {
                const previousMonth = value.startOf("month").subtract(1, "month");
                const nextMonth = value.startOf("month").add(1, "month");
                const previousDisabled = previousMonth.isBefore(dayjs().startOf("month"), "month");
                const nextDisabled = nextMonth.isAfter(maxBookingDate.startOf("month"), "month");
                return (
                  <Flex align="center" justify="space-between" style={{ padding: "4px 0 14px" }}>
                    <Button
                      disabled={previousDisabled}
                      type="text"
                      size="large"
                      icon={<ArrowLeftOutlined />}
                      onClick={() => moveCalendarMonth(value, onChange, -1)}
                    />
                    <Text strong style={{ margin: "auto", fontSize: 18, color: "#090D25" }}>
                      {formatCalendarMonth(value)}
                    </Text>
                    <Button
                      disabled={nextDisabled}
                      type="text"
                      size="large"
                      icon={<ArrowRightOutlined />}
                      onClick={() => moveCalendarMonth(value, onChange, 1)}
                    />
                  </Flex>
                );
              }}
            />

            <div>
              <Text
                type="secondary"
                style={{ fontSize: 12, letterSpacing: 1, textTransform: "uppercase" }}
              >
                Ledige tider {DATE_LONG_FMT.format(date.toDate())}
              </Text>
            </div>

            {dateBeyondHorizon ? (
              <Alert
                type="info"
                showIcon
                message="Lengre frem enn vi bestiller for"
                description={`Du kan bestille inntil ${horizonDays} dager frem i tid. Velg en tidligere dato.`}
              />
            ) : dateIsClosed ? (
              <Alert
                type="info"
                showIcon
                message={`${DATE_LONG_FMT.format(date.toDate())} er stengt`}
                description={reasonClosed ?? "Velg en annen dag."}
                action={
                  <Button size="small" onClick={jumpToNextOpenDay}>
                    Neste åpne dag
                  </Button>
                }
              />
            ) : slotsQ.isPending ? (
              <Spin size="small" />
            ) : slotsQ.error ? (
              <Alert
                type="error"
                showIcon
                message="Kunne ikke laste ledige tider"
                description={(slotsQ.error as Error).message}
              />
            ) : slotsQ.data?.slots.length === 0 ? (
              <Empty description="Ingen ledige tider denne dagen." style={{ marginTop: 0 }}>
                <Button onClick={jumpToNextOpenDay}>Finn neste ledige dag</Button>
              </Empty>
            ) : (
              <Space direction="vertical" size={8} style={{ display: "flex" }}>
                {slotsQ.data?.slots.map((s) => (
                  <SlotButton
                    key={s.startAt}
                    slot={s}
                    selected={selectedSlot === s.startAt}
                    onSelect={() => setSelectedSlot(s.startAt)}
                  />
                ))}
              </Space>
            )}
          </Space>
        </Card>
      ) : null}

      {activeStep === "detaljer" ? (
        <Card size="small" title="Forhåndsregistrer gjenstander">
          <Space direction="vertical" size="middle" style={{ display: "flex" }}>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Dette er valgfritt, men gjør kundebehandlingen raskere i butikk. Skriv bare en enkel
              liste over tingene du ønsker å ta med.
            </Paragraph>
            {preRegistrationItems.map((item, index) => (
              <div
                key={item.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  gap: 10,
                  alignItems: "center",
                }}
              >
                <Input
                  size="large"
                  value={item.description}
                  onChange={(e) =>
                    setPreRegistrationItems((items) =>
                      items.map((x) =>
                        x.id === item.id ? { ...x, description: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder={`Gjenstand ${index + 1}, f.eks. ring, armbånd eller kjede`}
                  maxLength={120}
                />
                <div>
                  <Button
                    size="large"
                    danger
                    onClick={() =>
                      setPreRegistrationItems((items) =>
                        items.length === 1
                          ? [makePreRegistrationDraft()]
                          : items.filter((x) => x.id !== item.id),
                      )
                    }
                  >
                    {preRegistrationItems.length === 1 ? "Tøm" : "Fjern"}
                  </Button>
                </div>
              </div>
            ))}
            <Button
              block
              onClick={() =>
                setPreRegistrationItems((items) => [...items, makePreRegistrationDraft()])
              }
            >
              Legg til gjenstand
            </Button>
          </Space>
        </Card>
      ) : null}

      {activeStep === "kontakt" ? (
        <Card size="small" title="Kontaktinfo">
          {loginRequired ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Vipps-innlogging kreves for dette stedet"
              description="Vi henter navn, telefon og e-post fra Vipps-profilen din."
              action={
                <Button
                  className="vipps-button"
                  size="small"
                  type="primary"
                  onClick={signInWithVipps}
                >
                  Logg inn med Vipps
                </Button>
              }
            />
          ) : null}
          <Form layout="vertical">
            <Form.Item
              label="Navn"
              required
              validateStatus={nameError && contact.name.length > 0 ? "error" : ""}
              help={contact.name.length > 0 ? (nameError ?? undefined) : undefined}
            >
              <Input
                value={contact.name}
                onChange={(e) => setContact({ ...contact, name: e.target.value })}
                autoComplete="name"
                size="large"
              />
            </Form.Item>
            <Form.Item
              label="E-post"
              required
              validateStatus={emailError && contact.email.length > 0 ? "error" : ""}
              help={contact.email.length > 0 ? (emailError ?? undefined) : undefined}
            >
              <Input
                type="email"
                value={contact.email}
                onChange={(e) => setContact({ ...contact, email: e.target.value })}
                autoComplete="email"
                inputMode="email"
                size="large"
              />
            </Form.Item>
            <Form.Item
              label="Telefon"
              required
              validateStatus={phoneError && contact.phone.length > 0 ? "error" : ""}
              help={contact.phone.length > 0 ? (phoneError ?? undefined) : undefined}
              extra="Vi bruker nummeret hvis vi må nå deg om avtalen."
            >
              <Input
                type="tel"
                inputMode="tel"
                value={contact.phone}
                onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                autoComplete="tel"
                placeholder="+47..."
                size="large"
              />
            </Form.Item>
            <Form.Item label="Notater (valgfritt)">
              <TextArea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="F.eks. type smykker, antall gjenstander, særlige ønsker."
                maxLength={2000}
                showCount
              />
            </Form.Item>
          </Form>
        </Card>
      ) : null}

      {activeStep === "bekreft" ? (
        <Card size="small" title="Sjekk og bekreft">
          <Space direction="vertical" size="middle" style={{ display: "flex" }}>
            {manifest && currentType && selectedSlot ? (
              <div>
                <Space size={6} wrap>
                  <Tag color="gold">{manifest.name}</Tag>
                  <Tag>{currentType.label}</Tag>
                  <Tag>{currentType.durationMinutes} min</Tag>
                  {currentType.priceNok && currentType.priceNok > 0 ? (
                    <Tag color="green">{formatNok(currentType.priceNok)}</Tag>
                  ) : null}
                </Space>
                <Text strong style={{ display: "block", marginTop: 8, fontSize: 16 }}>
                  {DATE_LONG_FMT.format(date.toDate())}, kl.{" "}
                  {TIME_FMT.format(new Date(selectedSlot))}
                </Text>
              </div>
            ) : null}
            <div>
              <Text type="secondary" style={{ fontSize: 12, letterSpacing: 1 }}>
                KONTAKT
              </Text>
              <div>{contact.name || "—"}</div>
              <div>{contact.email || "—"}</div>
              <div>{contact.phone || "—"}</div>
              {normalizedPreRegistrationItems.length > 0 ? (
                <div style={{ marginTop: 12 }}>
                  <Text type="secondary" style={{ fontSize: 12, letterSpacing: 1 }}>
                    FORHÅNDSREGISTRERT
                  </Text>
                  <Space direction="vertical" size={4} style={{ display: "flex", marginTop: 4 }}>
                    {normalizedPreRegistrationItems.map((item, index) => (
                      <div key={item.id}>
                        <Text strong>{item.description || `Gjenstand ${index + 1}`}</Text>
                        <Text type="secondary">
                          {[item.description ? null : "Ingen navn"].filter(Boolean).join(" · ")}
                        </Text>
                      </div>
                    ))}
                  </Space>
                </div>
              ) : null}
              {notes ? (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12, letterSpacing: 1 }}>
                    NOTATER
                  </Text>
                  <Paragraph style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}>{notes}</Paragraph>
                </div>
              ) : null}
            </div>
            <ErrorAlert error={error} />
          </Space>
        </Card>
      ) : null}

      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          background: "#ffffff",
          borderTop: "1px solid #e7e3d8",
          padding: "12px 16px calc(12px + env(safe-area-inset-bottom))",
          zIndex: 20,
        }}
      >
        <div
          style={{
            maxWidth: 720,
            margin: "0 auto",
            display: "flex",
            gap: 12,
            alignItems: "center",
          }}
        >
          <Button
            size="large"
            disabled={stepIndex === 0}
            onClick={() => setStepIndex(Math.max(0, stepIndex - 1))}
          >
            Tilbake
          </Button>
          {selectedSlot && manifest && currentType ? (
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 12px",
                border: "1px solid #eadfcd",
                borderRadius: 14,
                background: "#fbf7ef",
              }}
            >
              <Tag color="gold" style={{ marginInlineEnd: 0 }}>
                {TIME_FMT.format(new Date(selectedSlot))}
              </Tag>
              <div style={{ minWidth: 0 }}>
                <Text strong style={{ display: "block", lineHeight: 1.15 }}>
                  {currentType.label}
                </Text>
                <Text
                  type="secondary"
                  style={{
                    display: "block",
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: 12,
                  }}
                >
                  {date.format("DD.MM.YYYY")}
                </Text>
              </div>
            </div>
          ) : (
            <div
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 13,
                color: "#6b6b6b",
              }}
            >
              Steg {stepIndex + 1} av {steps.length}
            </div>
          )}
          <Button
            type="primary"
            size="large"
            loading={createMutation.isPending}
            disabled={activeStep === "bekreft" ? !canSubmit : !stepValid(activeStep)}
            onClick={advance}
          >
            {activeStep === "bekreft"
              ? currentType?.priceNok && currentType.priceNok > 0
                ? `Bekreft · ${formatNok(currentType.priceNok)}`
                : "Bekreft"
              : "Neste"}
          </Button>
        </div>
      </div>
    </Content>
  );
}

function SlotButton({
  slot,
  selected,
  onSelect,
}: {
  slot: { startAt: number; endAt: number };
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <Button
      type={selected ? "primary" : "default"}
      onClick={onSelect}
      size="large"
      block
      style={{
        height: 60,
        borderColor: selected ? undefined : "#e5ded2",
        boxShadow: selected ? "0 10px 24px rgba(0, 0, 0, 0.12)" : "none",
      }}
    >
      <span
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        <span style={{ fontSize: 18, fontWeight: 700 }}>
          {TIME_FMT.format(new Date(slot.startAt))}
        </span>
        <span style={{ opacity: 0.72 }}>{TIME_FMT.format(new Date(slot.endAt))}</span>
      </span>
    </Button>
  );
}
